/**
 * SearchBarOptimizations - Helper functions for SearchBar component
 * 
 * Extracted to reduce SearchBar complexity and improve performance
 */

// SearchSuggestion interface defined locally
interface SearchSuggestion {
  id: string;
  text: string;
  category?: string;
}

/**
 * Creates search result from suggestion
 */
export const createSearchResult = (suggestion: SearchSuggestion) => ({
  id: suggestion.id,
  title: suggestion.text,
  category: suggestion.category || 'search',
  relevanceScore: 1
});

/**
 * Search bar state interface
 */
export interface SearchBarState {
  query: string;
  isLoading: boolean;
  isOpen: boolean;
  results: unknown[];
  suggestions: SearchSuggestion[];
  error: string | null;
  selectedIndex: number;
}

/**
 * Gets ARIA descriptors for accessibility
 */
export const getAriaDescriptors = (state: SearchBarState, componentId: string) => ({
  ariaExpanded: state.isOpen && (state.suggestions.length > 0 || state.isLoading || Boolean(state.error)),
  ariaActivedescendant: state.selectedIndex >= 0 ? `${componentId}-option-${state.selectedIndex}` : undefined,
  ariaDescribedby: state.error ? `${componentId}-error` : undefined
});

/**
 * Determines which UI elements should be visible
 */
export const getVisibilityStates = (state: SearchBarState) => ({
  shouldShowClearButton: state.query && !state.isLoading,
  shouldShowLoadingSpinner: state.isLoading,
  shouldShowSearchIcon: !state.isLoading
});

/**
 * Gets screen reader announcement text
 */
export const getScreenReaderAnnouncement = (state: SearchBarState): string => {
  if (state.isLoading) return 'Searching...';
  if (state.error) return `Search error: ${state.error}`;
  if (state.suggestions.length > 0) return `${state.suggestions.length} suggestions available`;
  return '';
};

/**
 * Optimized input change handler factory
 */
export const createInputChangeHandler = (
  setState: (updater: (prev: SearchBarState) => SearchBarState) => void
) => (event: React.ChangeEvent<HTMLInputElement>) => {
  setState(prev => ({ 
    ...prev, 
    query: event.target.value, 
    isOpen: true, 
    selectedIndex: -1 
  }));
};

/**
 * Optimized clear handler factory
 */
export const createClearHandler = (
  handleKeyboardNavigation: (action: string) => void,
  inputRef: React.RefObject<HTMLInputElement>
) => () => {
  handleKeyboardNavigation('clear');
  inputRef.current?.focus();
};

/**
 * Optimized suggestion hover handler factory
 */
export const createSuggestionHoverHandler = (
  setState: (updater: (prev: SearchBarState) => SearchBarState) => void
) => (index: number) => {
  setState(prev => ({ ...prev, selectedIndex: index }));
};

/**
 * Gets component configuration for display
 */
export const getDisplayConfiguration = (state: SearchBarState) => {
  const showDropdown = state.isOpen && (
    state.suggestions.length > 0 || 
    state.isLoading || 
    Boolean(state.error)
  );
  
  const componentId = 'search-bar';
  const dropdownId = `${componentId}-dropdown`;
  const inputId = `${componentId}-input`;
  
  return {
    showDropdown,
    componentId,
    dropdownId,
    inputId
  };
};

/**
 * Optimized effect factory for auto-focus
 */
export const createAutoFocusEffect = (
  autoFocus: boolean,
  inputRef: React.RefObject<HTMLInputElement>
) => () => {
  if (autoFocus && inputRef.current) {
    inputRef.current.focus();
  }
};

/**
 * Performance optimization utilities
 */
export const SearchBarOptimizations = {
  createSearchResult,
  getAriaDescriptors,
  getVisibilityStates,
  getScreenReaderAnnouncement,
  createInputChangeHandler,
  createClearHandler,
  createSuggestionHoverHandler,
  getDisplayConfiguration,
  createAutoFocusEffect
};